import React, { useEffect, useState } from 'react'
import { withTranslation } from 'react-i18next'
import * as UIcons from '@iconscout/react-unicons'
import useSWR, { mutate } from 'swr'
import dayjs from 'dayjs'
import { CSVLink } from 'react-csv'
import Btn from 'components/Btn'
import PageHeader from '../shared/PageHeader'
import BranchesStats from './components/BranchesStats'
import BranchFilterBar from './components/BranchFilterBar'
import BranchesTable from './components/BranchesTable'
import AddBranch from './components/AddBranch'
import usePermissions from 'hooks/usePermissions'

export default withTranslation()(function Branches({ t }) {
  const { data, isValidating } = useSWR('merchants/branches')
  const { data: contactsRes } = useSWR('merchants/contacts')
  const { isPermitted } = usePermissions()
  const [q, setQ] = useState('')
  const [location, setLocation] = useState('')
  const [status, setStatus] = useState('')
  const [showAdd, setShowAdd] = useState(false)
  const [lastUpdated, setLastUpdated] = useState(dayjs())

  const branches = data?.data ?? []
  const contacts = contactsRes?.data ?? []
  const contactNameById = contacts.reduce((acc, contact) => ({ ...acc, [contact.id]: `${contact.first_name ?? ''} ${contact.last_name ?? ''}`.trim() }), {})
  const locations = [...new Set(branches.map((branch) => branch.location).filter(Boolean))]

  useEffect(() => {
    if (!isValidating) setLastUpdated(dayjs())
  }, [isValidating])

  const refresh = () => {
    mutate('merchants/branches')
    mutate('merchants/contacts')
    mutate('pos')
  }

  const csvData = branches.map((branch) => ({
    name: branch.name,
    location: branch.location,
    branch_manager: contactNameById[branch.branch_manager_id] ?? '',
    contact: branch.phone ?? '',
    status: branch.status ?? 'active'
  }))

  return (
    <section className="w-full">
      <PageHeader
        crumbs={[
          { name: t('settings.hub.title'), url: '/settings' },
          { name: t('settings.business_organization.title'), url: '/settings/business-organization' },
          { name: t('settings.branch.table.title') }
        ]}
        title={t('settings.branch.page_title')}
        right={
          <div className="flex items-center gap-2">
            <span className="text-sm text-secondary whitespace-nowrap">
              {t('settings.branch.last_update', { date: lastUpdated.format('D MMM YY') })}
            </span>

            <button
              type="button"
              onClick={refresh}
              className="w-9 h-9 rounded-full border border-border-lines flex items-center justify-center hover:bg-background shrink-0"
            >
              <UIcons.UilRedo className="text-secondary" size={16} />
            </button>

            <CSVLink
              data={csvData}
              filename="branches.csv"
              className="flex items-center gap-1.5 border border-border-lines rounded-full h-9 px-4 text-sm font-medium text-dark-blue whitespace-nowrap shrink-0"
            >
              <UIcons.UilExport size={16} /> {t('misc.export')}
            </CSVLink>

            <Btn
              content={() => <span className="flex items-center gap-1.5"><UIcons.UilPlus size={16} /> {t('settings.branch.add')}</span>}
              className="text-white bg-primary rounded-full h-9 px-4 font-medium"
              click={() => setShowAdd(true)}
              disabled={!isPermitted?.create_branches}
            />
          </div>
        }
      />

      <BranchesStats />

      <BranchFilterBar
        q={q}
        onChangeQ={setQ}
        locations={locations}
        location={location}
        onChangeLocation={setLocation}
        status={status}
        onChangeStatus={setStatus}
      />

      <BranchesTable q={q} location={location} status={status} />

      {
        showAdd && (
          <AddBranch
            handleClick={() => setShowAdd(false)}
            onSaved={() => mutate('merchants/branches')}
          />
        )
      }
    </section>
  )
})
